import { setUserAffiliations } from "./useraffiliationstore";
import { setUser } from "./userstore";

/* Sessions are shared across the browser's tabs, so the whole session lives in
   localStorage - a second tab is the same login, not an anonymous visitor. */
const storage = localStorage;
export const authStorageKey = 'auth';

type StoredAuth = {
    token: string;
    refreshToken: string;
    refreshTokenExpiresAtUtc: string;
};

function getStoredAuth(): StoredAuth | null {
    const json = storage.getItem(authStorageKey);
    if (!json) {
        return null;
    }

    try {
        return JSON.parse(json);
    } catch {
        // Persisted storage outlives the code that wrote it - an unreadable
        // entry is no session, not a crash on every caller.
        return null;
    }
}

export function setTokens(bearerToken: string, refreshToken: string, refreshTokenExpiresAtUtc: string) {
    const value: StoredAuth = {
        token: bearerToken,
        refreshToken: refreshToken,
        refreshTokenExpiresAtUtc: refreshTokenExpiresAtUtc
    };
    storage.setItem(authStorageKey, JSON.stringify(value));
}

/* Ends the session and drops everything cached about the person who owned it.
   Storage now outlives the browser tab, so leaving any of it behind means the
   next person to log in on this browser inherits the previous one's data. */
export function clearSession() {
    storage.removeItem(authStorageKey);
    setUser(null);
    setUserAffiliations(null);
}

export function getBearerToken(): string | null {
    return getStoredAuth()?.token ?? null;
}

export function getRefreshToken(): string | null {
    return getStoredAuth()?.refreshToken ?? null;
}

export function hasValidToken(): boolean {
    const auth = getStoredAuth();
    if (!auth?.token || !auth?.refreshTokenExpiresAtUtc) {
        return false;
    }

    return new Date(auth.refreshTokenExpiresAtUtc).getTime() > Date.now();
}

function decodeJwtExpiryMs(token: string): number | null {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}

export function isAccessTokenExpiringSoon(bufferMs = 30_000): boolean {
    const token = getBearerToken();
    if (!token) return true;

    const expiryMs = decodeJwtExpiryMs(token);
    if (expiryMs === null) return false;

    return expiryMs - bufferMs <= Date.now();
}
